/**
 * [Weight Rebalance + Edge Surfaces §2a] PGFEdgeBadge
 *
 * Compact badge - UNDERVALUED (green) / OVERVALUED (orange) / FAIR VALUE
 * (grey). Renders inline; tap-through reveals dollar comparison + the
 * driving axis reason. Founder-locked colors:
 *
 *   • UNDERVALUED → #00C853
 *   • OVERVALUED  → #FF6B00  (PGF accent orange, not pure red)
 *   • FAIR VALUE  → neutral grey
 *
 * Copy-driven, NOT LLM-generated. Every string comes from the backend
 * `pgf_edge` object (engines/analytics/pgf_edge.py).
 */
import React, { useState } from "react";

const BADGE_STYLE = {
  UNDERVALUED: {
    ring: "border-[#00C853]/60 bg-[#00C853]/15",
    label: "text-[#00C853]",
    dot: "bg-[#00C853]",
  },
  OVERVALUED: {
    ring: "border-[#FF6B00]/60 bg-[#FF6B00]/15",
    label: "text-[#FF6B00]",
    dot: "bg-[#FF6B00]",
  },
  "FAIR VALUE": {
    ring: "border-white/[0.20] bg-white/[0.05]",
    label: "text-white/70",
    dot: "bg-white/40",
  },
  // [HOTFIX Availability v3 2026-07-10] Grey grey badge for players
  // whose axes don't clear the confidence threshold - score is NULL,
  // dollar delta suppressed.
  "INSUFFICIENT DATA": {
    ring: "border-white/[0.15] bg-white/[0.04]",
    label: "text-white/55",
    dot: "bg-white/30",
  },
  // [HOTFIX Edge Accuracy 2026-07] Grey badge for players without live
  // ADP - PGF axes still surface on the card, but the dollar delta
  // and market-anchored comparison are suppressed.
  "NO MARKET DATA": {
    ring: "border-white/[0.15] bg-white/[0.04]",
    label: "text-white/55",
    dot: "bg-white/30",
  },
};

export function PGFEdgeBadge({ edge, size = "md", compact = false, asChild = false }) {
  const [expanded, setExpanded] = useState(false);
  if (!edge || !edge.badge) return null;

  const style = BADGE_STYLE[edge.badge] || BADGE_STYLE["FAIR VALUE"];
  const sizeCls = size === "sm" ? "px-2 py-0.5 text-[10px]" : "px-2.5 py-1 text-xs";
  // Defensive numeric coercion - backend currently always returns
  // numbers but a null could crash .toFixed(). Per iteration_51 nit #4.
  const pgfVal = edge.pgf_value ?? 0;
  const mktVal = edge.market_value ?? 0;
  const edgeDol = edge.edge_dollars ?? 0;
  const edgePct = edge.edge_pct ?? 0;
  // HOTFIX Edge Accuracy: no dollar delta for market-less rows.
  // HOTFIX v3: no dollar delta for insufficient-data rows either.
  const isNoMarket = edge.badge === "NO MARKET DATA";
  const isInsuf = edge.badge === "INSUFFICIENT DATA";
  const suppressDollars = isNoMarket || isInsuf;

  const summary = (
    <div className="flex items-center gap-1.5">
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      <span className="font-display font-bold uppercase tracking-wide">{edge.badge}</span>
      {!compact && !suppressDollars && (
        <span className={`ml-1 font-mono text-[10px] opacity-80 ${style.label}`}>
          ${pgfVal.toFixed(0)} vs ${mktVal.toFixed(0)}
        </span>
      )}
    </div>
  );

  // [Sprint iter57 fix] When this badge is nested inside a <button>
  // (EdgeRow), render as a <span> to avoid nested-button hydration warnings.
  // Popover expansion is disabled in that mode - the parent row's click
  // opens EdgePlayerModal which surfaces the same detail.
  if (asChild) {
    return (
      <span
        className={`inline-flex items-center rounded-md border transition-all ${style.ring} ${style.label} ${sizeCls}`}
        data-testid={`pgf-edge-badge-${edge.badge.toLowerCase().replace(" ", "-")}`}
      >
        {summary}
      </span>
    );
  }

  return (
    <div className="inline-flex flex-col" data-testid="pgf-edge-badge">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className={`inline-flex items-center rounded-md border transition-all ${style.ring} ${style.label} ${sizeCls} hover:brightness-110`}
        data-testid={`pgf-edge-badge-${edge.badge.toLowerCase().replace(" ", "-")}`}
        aria-expanded={expanded}
      >
        {summary}
      </button>
      {expanded && (
        <div
          className="mt-1 max-w-xs rounded-lg border border-white/10 bg-black/70 backdrop-blur-md p-2.5 text-[11px] text-white/85 leading-snug space-y-1"
          data-testid="pgf-edge-badge-detail"
        >
          {!isNoMarket && !isInsuf && (
            <>
              <div className="flex items-center justify-between font-mono">
                <span className="text-white/60">PGF Value</span>
                <span className={style.label}>${pgfVal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between font-mono">
                <span className="text-white/60">Market (ADP)</span>
                <span className="text-white/80">${mktVal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between font-mono border-t border-white/10 pt-1">
                <span className="text-white/60">Edge</span>
                <span className={style.label}>
                  {edgeDol >= 0 ? "+" : ""}${edgeDol.toFixed(2)}
                  {" "}
                  ({(edgePct * 100).toFixed(0)}%)
                </span>
              </div>
            </>
          )}
          {edge.driving_axis && (
            <div className="text-white/70 pt-1 uppercase tracking-widest text-[9px]">
              Edge driver:{" "}
              <span className="lowercase text-white/90">{edge.driving_axis}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PGFEdgeBadge;
